// /forgot-password: ask for a password reset link (registration plan S2).
//
// POST /auth/forgot-password (1.0's reset, the same one league admins use)
// answers the same 202 for any address. The page composes its own
// confirmation from the typed address ("If an account exists for <email>,
// …"), the same for every address, and never shows the response body. Only
// a network error or a 429 (they don't depend on the address) shows an error.
//
// From the login page it sends `portal: 'platform'` (a super admin's link,
// opening on Ops at /reset-password#<token>). From the invite page (router
// state `fromInvite`) it sends no portal: the invitee isn't a super admin
// yet, and a platform reset for them would be dropped silently; their link
// may open on a league's admin.

import { useState, type FormEvent } from 'react'
import { Link, useLocation } from 'react-router-dom'
import { api } from '@/api'
import { isApiError } from '@/api/client'
import { Button, FormField, InlineAlert, TextInput } from '@/components/ui'
import { useDocumentTitle } from '@/lib/hooks'
import { AuthCard, authLinkClass } from './AuthCard'

export default function ForgotPasswordPage() {
  useDocumentTitle('Reset your password')
  const location = useLocation()
  const state = (location.state ?? {}) as { email?: unknown; fromInvite?: unknown }
  const fromInvite = typeof state.fromInvite === 'string' && state.fromInvite.startsWith('/invite/') ? state.fromInvite : null
  const [email, setEmail] = useState(typeof state.email === 'string' ? state.email : '')
  const [pending, setPending] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [sent, setSent] = useState<string | null>(null)
  const [resent, setResent] = useState(false)

  const send = async () => {
    setResent(false)
    const addr = email.trim()
    if (!addr) {
      setError('Enter your email address.')
      return
    }
    const again = sent !== null
    setError(null)
    setPending(true)
    try {
      // The body is ignored: any 2xx shows the page's own confirmation.
      await api.auth.forgotPassword(addr, fromInvite ? null : 'platform')
      setSent(addr)
      if (again) setResent(true)
    } catch (err) {
      if (isApiError(err) && err.status === 429) setError('Too many attempts. Please wait a minute and try again.')
      else if (isApiError(err) && err.code === 'network_error') setError(err.message)
      else setError('Something went wrong. Try again.')
    } finally {
      setPending(false)
    }
  }

  const submit = (e: FormEvent) => {
    e.preventDefault()
    void send()
  }

  return (
    <AuthCard>
      {sent ? (
        <div className="grid gap-3">
          <h1 className="text-[1.75rem] font-extrabold leading-none text-ink">Check your email</h1>
          <p role="status" className="break-words text-[13.5px] text-ink [overflow-wrap:anywhere]">
            If an account exists for <span className="font-semibold">{sent}</span>, you&apos;ll receive an email with a link to reset your password.
          </p>
          {fromInvite ? <p className="text-[13px] text-muted">Once you&apos;ve reset your password, open your invitation again to join.</p> : null}
          {/* leading-5: the inline button inherits it, so -my-3 py-3 makes a 44 px tap target (20 + 24). */}
          <p className="text-xs leading-5 text-muted">
            Didn&apos;t receive it? Check your spam folder or{' '}
            <button
              type="button"
              onClick={() => void send()}
              disabled={pending}
              aria-busy={pending}
              className="-my-3 inline-block py-3 font-semibold text-accent hover:underline disabled:opacity-60"
            >
              send it again
            </button>
            .
          </p>
          {resent && !pending ? (
            <p role="status" className="text-xs text-muted">
              Email sent again.
            </p>
          ) : null}
          {error ? <InlineAlert tone="bad">{error}</InlineAlert> : null}
        </div>
      ) : (
        <>
          <h1 className="text-[1.75rem] font-extrabold leading-none text-ink">Reset your password</h1>
          <p className="mt-2 text-[13px] text-muted">Enter the email address for your account and we&apos;ll send you a link to reset your password.</p>
          {error ? (
            <InlineAlert tone="bad" className="mt-5">
              {error}
            </InlineAlert>
          ) : null}
          <form onSubmit={submit} noValidate className="mt-5 grid gap-4">
            <FormField label="Email">
              {({ id, describedBy }) => (
                <TextInput id={id} aria-describedby={describedBy} type="email" autoComplete="username" inputMode="email" autoFocus value={email} onChange={(e) => setEmail(e.target.value)} disabled={pending} />
              )}
            </FormField>
            <Button type="submit" variant="primary" block loading={pending} className="h-10">
              Send reset link
            </Button>
          </form>
        </>
      )}
      <div className="mt-3 flex flex-wrap items-center justify-between gap-x-4">
        <Link to="/login" className={authLinkClass}>
          Back to sign in
        </Link>
        {fromInvite ? (
          <Link to={fromInvite} className={authLinkClass}>
            Back to your invitation
          </Link>
        ) : null}
      </div>
    </AuthCard>
  )
}
